import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, Col, Row } from 'react-bootstrap';
import { LoadingStatusWrapper, ToggleButton, Switch as SwitchComponent } from 'sharedComponents';
import { useTranslation } from 'react-i18next';
import { IconInfoFilled } from 'svgIcons/MotionPortalIcons';
import KPITrendChartListComponent from './KPITrendChartList';
import { DriveStatus } from './SingleChartView/DriveStatus';
import { getDriveStatusData, getDriveOverAllStatus, combinedTrendCharts, getToggleOptions, conditionInsightsTabs, getCoolingAnomalyKPI, getEnergyAnomalyKPI } from './helpers';
import colors from 'theme/_colors.scss';
import './style.scss';
import ConditionInsightsLegend from './ConditionInsightsLegend';
import MotorAnomalyDetectionComponent from './CommonCharts/MotorAnomalyDetection/MotorAnomalyDetectionComponent';
import DriveAnomalyDetectionComponent from './CommonCharts/DriveAnomalyDetection/DriveAnomalyDetectionComponent';
import { isSmSeAsset } from '../helpersV2';
import useUserCapability from 'sharedHooks/useUserCapability';
import { MACHSENSOR, driveAnomalyTabs, ACS1000_COOLING_ANOMALY_KEY } from './constants';
import { useNewCapability } from 'sharedHooks';
import { apiCallStatus, ASSET_TYPES_MS } from 'helpers/constants';

const ConditionInsightsComponent = ({
    model: {
        conditionInsights,
        assetDetails,
        activeTab,
        selectedAsset,
        kpiFromSummary
    },
    actions
}) => {
    const userCapabilities = useUserCapability();
    const newCapabilities = useNewCapability();

    useEffect(() => {
        return () => {
            if (assetDetails?.id) {
                // Reset zoom when unmount the component
                actions.setIsZoomed(false);
                actions.clearStore();
                actions.setClickedConditionIndex(null);
            }
        };
    }, [assetDetails?.id]);

    const { t: translate } = useTranslation();
    const driveKPIS = useMemo(() => getDriveStatusData(conditionInsights?.driveKPI), [conditionInsights?.driveKPI]);
    const overAllDriveStatus = useMemo(() => getDriveOverAllStatus(conditionInsights?.driveKPI), [conditionInsights?.driveKPI]);
    const trendChartList = useMemo(() => combinedTrendCharts(conditionInsights?.trendKPI), [conditionInsights?.trendKPI]);
    const anomalyDetectionKPIList = conditionInsights?.anomalyDetectionKPI;
    const coolingAnomalyKPI = useMemo(() => getCoolingAnomalyKPI(anomalyDetectionKPIList), [anomalyDetectionKPIList]);
    const energyAnomalyKPI = useMemo(() => getEnergyAnomalyKPI(anomalyDetectionKPIList), [anomalyDetectionKPIList]);

    const anomalyDetectionKPI = anomalyDetectionKPIList?.[0];
    const SmseAsset = isSmSeAsset(assetDetails?.details?.assetType);
    const toggleOptions = getToggleOptions();
    const showConditionForecast = conditionInsights?.showConditionForecast || false;

    const [activeDriveTab, setActiveDriveTab] = useState(driveAnomalyTabs.COOLING);
    const isCoolingAnomalyActive = !!coolingAnomalyKPI
        && activeDriveTab === driveAnomalyTabs.COOLING
        && coolingAnomalyKPI?.key !== ACS1000_COOLING_ANOMALY_KEY;

    const renderDriveStatusSection = () => {
        if (conditionInsights?.driveKPI) {
            return (
                <Row className='conditional-insights-drive-status-component'>
                    <Col className='drive-status-column'>
                        <DriveStatus
                            model={{
                                selectedTrendsData: {
                                    data: driveKPIS,
                                    loadingStatus: conditionInsights?.loadingStatus,
                                    zoom: { isZoomed: conditionInsights?.isZoomed },
                                    chartID: 'drive-status'
                                }
                            }}
                            title={translate('ABB.Powertrain.Frontend.conditionDriveChartTitle')}
                            conditionStatus={overAllDriveStatus}
                            assetDetails={assetDetails}
                            actions={actions}
                        />
                    </Col>
                </Row>
            );
        }
        return null;
    };

    const handleTabSelection = (tab) => {
        actions.setActiveTab(tab);
    };

    const handleForecastToggle = (enabled) => {
        actions.toggleConditionForecast(enabled);
    };

    const renderEmptyStatusView = () => {
        return (
            <div className='conditional-insight-status-message'>
                <IconInfoFilled width={'32px'} height={'32px'} color={colors.nero} />
                <span>{translate('ABB.Powertrain.Frontend.conditionDetailsOKMessage')}</span>
            </div>
        );
    };

    const renderOverAllKPIView = (isMachineGuardianAsset, AnomalyDetectionAvailable, shouldShowAnomalyTab) => {
        if (shouldShowAnomalyTab && !isMachineGuardianAsset && AnomalyDetectionAvailable && activeTab === conditionInsightsTabs.ANOMALY_DETECTION) {
            // Determine if it's actually drive anomaly based on trend data
            const isDriveAnomaly = !SmseAsset && (anomalyDetectionKPI?.trend?.length > 0 || !!coolingAnomalyKPI || !!energyAnomalyKPI);

            if (isDriveAnomaly) {
                return (
                    <DriveAnomalyDetectionComponent
                        coolingAnomalyKPI={coolingAnomalyKPI}
                        energyAnomalyKPI={energyAnomalyKPI}
                        loadingStatus={conditionInsights?.loadingStatus}
                        actions={actions}
                        title={translate('ABB.Powertrain.Frontend.coolingAnomalyChartTitle')}
                        levelLabel={translate('ABB.Powertrain.Frontend.levelLabel')}
                        assetDetails={assetDetails}
                        isZoomed={conditionInsights?.isZoomed}
                        selectedAsset={selectedAsset}
                        initialTab={conditionInsights?.driveAnomalyActiveTab}
                        onTabChange={setActiveDriveTab}
                    />
                );
            }

            return (
                <MotorAnomalyDetectionComponent
                    anomalyDetectionKPI={anomalyDetectionKPI}
                    loadingStatus={conditionInsights?.loadingStatus}
                    actions={actions}
                    title={translate('ABB.Powertrain.Frontend.anomalyDetectionTab')}
                    levelLabel={translate('ABB.Powertrain.Frontend.levelLabel')}
                    assetDetails={assetDetails}
                    isZoomed={conditionInsights?.isZoomed}
                    selectedAsset={selectedAsset}
                />
            );
        } else {
            return (
                <React.Fragment>
                    {renderDriveStatusSection()}
                    <KPITrendChartListComponent
                        trendParams={trendChartList}
                        loadingStatus={conditionInsights?.loadingStatus}
                        actions={actions}
                        isZoomed={conditionInsights?.isZoomed}
                        assetId={assetDetails?.id}
                        assetDetails={assetDetails}
                        showConditionForecast={showConditionForecast}
                    />
                </React.Fragment>
            );
        }
    };

    const isConditionDataAvailable = trendChartList?.length || driveKPIS?.length || anomalyDetectionKPI?.conditionTrends?.length;
    const isMotorAsset = assetDetails?.details?.assetType?.assetType === ASSET_TYPES_MS.MOTOR;
    const isDriveAsset = assetDetails?.details?.assetType?.assetType === ASSET_TYPES_MS.DRIVE;
    const AnomalyDetectionAvailable = isDriveAsset || isMotorAsset;
    const hasMotorAnomalyDetection = userCapabilities.hasMotorAnomalyDetection();
    const hasCoolingAnomalyDetection = userCapabilities.hasDriveAnomalyDetection();
    const hasEnergyOutlierDetection = userCapabilities.hasDriveEnergyOutlierDetection();
    const hasACS1000CoolingAnomalyDetection = userCapabilities.hasDriveACS1000AnomalyDetection();
    const hasDriveAnomalyDetection = hasCoolingAnomalyDetection || hasEnergyOutlierDetection || hasACS1000CoolingAnomalyDetection;
    const hasMotorCapraRecommendations = newCapabilities?.hasMotorCapraRecommendationsCapability?.() || false;
    const isMachineGuardianAsset = assetDetails?.details?.sensorType === MACHSENSOR;

    // Check if capability matches asset type
    const shouldShowAnomalyTab = hasMotorAnomalyDetection && isMotorAsset || hasDriveAnomalyDetection && isDriveAsset;

    // Determine if current anomaly is actually drive anomaly based on trend data
    const isActuallyDriveAnomaly = !SmseAsset;

    // scroll to the selected KPI chart when KPI is clicked from summary tab
    useEffect(() => {
        if (kpiFromSummary && isConditionDataAvailable) {
            const element = document.getElementById(kpiFromSummary);
            if (element) {
                const timeoutId = setTimeout(() => { /* adding timeout to ensure chat is loaded */
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 1500);
                return () => clearTimeout(timeoutId);
            }
        }
    }, [kpiFromSummary, isConditionDataAvailable]);

    const shouldShowForecastSwitch = isMotorAsset && hasMotorCapraRecommendations && activeTab !== conditionInsightsTabs.ANOMALY_DETECTION;

    return (
        <Container fluid id='conditional-insights-parameters-container'>
            <LoadingStatusWrapper transparent useCustomAnchor indicatorConfig={{ placement: 'sticky' }} loadingStatus={[conditionInsights?.loadingStatus]}>
                {isConditionDataAvailable ?
                    <>
                        <div className={`conditional-insights-header${shouldShowAnomalyTab && !isMachineGuardianAsset && AnomalyDetectionAvailable ? ' with-toggle' : ''}`}>
                            {shouldShowAnomalyTab && !isMachineGuardianAsset && AnomalyDetectionAvailable &&
                                <ToggleButton
                                    options={toggleOptions}
                                    onSelected={handleTabSelection}
                                    selectedValue={activeTab}
                                />
                            }
                            {shouldShowForecastSwitch && !isMachineGuardianAsset && <div className='condition-forecast-toggle'>
                                <SwitchComponent
                                    checked={showConditionForecast}
                                    onChange={handleForecastToggle}
                                    disableHandleIcons
                                />
                                <span className='condition-forecast-toggle__label'>
                                    {translate('ABB.Powertrain.Frontend.conditionForecast')}
                                </span>
                            </div>}
                            <ConditionInsightsLegend
                                activeTab={activeTab}
                                assetDetails={assetDetails?.details}
                                hasDriveAnomalyDetection={isActuallyDriveAnomaly}
                                isCoolingAnomalyActive={isCoolingAnomalyActive}
                            />
                        </div>
                        {renderOverAllKPIView(isMachineGuardianAsset, AnomalyDetectionAvailable, shouldShowAnomalyTab)}
                    </>
                    : null
                }
            </LoadingStatusWrapper>
            {conditionInsights?.loadingStatus !== apiCallStatus.LOADING && !isConditionDataAvailable ?
                renderEmptyStatusView()
                : null}
        </Container >);
};

ConditionInsightsComponent.propTypes = {
    model: PropTypes.shape({
        activeTab: PropTypes.string,
        conditionInsights: PropTypes.object,
        assetDetails: PropTypes.object,
        selectedAsset: PropTypes.object,
        kpiFromSummary: PropTypes.string
    }),
    actions: PropTypes.shape({
        setIsZoomed: PropTypes.func,
        clearStore: PropTypes.func,
        setActiveTab: PropTypes.func,
        setClickedConditionIndex: PropTypes.func,
        toggleConditionForecast: PropTypes.func,
        getRetrainingProgress: PropTypes.func,
        reloadConditionInsights: PropTypes.func,
        getAssetConditionIndicesRequest: PropTypes.func
    })
};
export default ConditionInsightsComponent;
